import {
  allSectionsOf,
  evaluateConditions,
  forEachSectionField,
  scopeValuesToInstance,
  uniquenessGroupsOf,
  valueKeyFor,
} from '@bmo/form-core';

import type { FormSection, WorkflowField, WorkflowStep, WorkflowTemplate } from '@/api/workflows';

import { resolvePrefillSourceInstance } from './conditionalPrefill';
import type { FieldValue } from './FieldRenderer';

/** What {@link fieldVisibleForUniqueness} needs to judge one field. FormShell
 *  supplies these from its own memos; tests build them directly. */
export type UniquenessVisibilityContext = {
  steps: readonly WorkflowStep[];
  /** Step-level gates, precomputed by FormShell. A step absent from the map is visible. */
  stepVisibility: ReadonlyMap<string, boolean>;
  values: Record<string, FieldValue>;
  /** Every section in the template — lets a repeatable's mirror family resolve. */
  allSections: readonly FormSection[];
};

/**
 * Is `field` — in repeatable instance `instance` — visible for the purposes of
 * uniqueness? Shared by BOTH the on-blur check and the workflow-wide walk, so
 * an inline error can never accuse a peer that `findAllUniquenessConflicts`
 * wouldn't count (which would paint red while Next stayed enabled).
 *
 * A FIELD gate inside a repeatable section is read against THAT instance's
 * values, exactly as FormRenderer reads it. An unscoped condition resolves by
 * bare field id (`values[c.fieldId]`), but inside a repeatable the value only
 * ever lives at `inst:<sectionId>:<idx>:<fieldId>` — so evaluating against the
 * raw map read every such gate as UNSET, and a positive operator on an unset
 * source reports the field hidden. Both walkers skip hidden fields, so every
 * uniqueness-tagged field gated from inside its own repeatable section was
 * silently exempt from the check in both directions: RESP's beneficiary email
 * (gated `age_years_ge 18` on the beneficiary DOB) and caregiver email (gated
 * `equals other` on the caregiver radio) rendered side by side, both tagged
 * `respEmail`, and sharing one address raised nothing.
 *
 * `scopeValuesToInstance` returns the map untouched for a non-repeatable
 * section, so the ordinary cross-step case is unchanged.
 *
 * SECTION conditions stay on the raw map — a repeatable section's own gate
 * refers to fields OUTSIDE itself (that is what makes it a gate), and this
 * mirrors FormRenderer, which scopes only the per-field evaluation.
 */
export function fieldVisibleForUniqueness(
  ctx: UniquenessVisibilityContext,
  field: WorkflowField,
  stepId: string,
  instance = 0,
): boolean {
  const step = ctx.steps.find((s) => s.id === stepId);
  if (!step) return false;
  if (!(ctx.stepVisibility.get(stepId) ?? true)) return false;
  const section = step.sections.find((sec) =>
    sec.rows.some((r) => r.columns.some((c) => c.field?.id === field.id)),
  );
  if (
    section &&
    (section.conditions?.length ?? 0) > 0 &&
    !evaluateConditions(section.conditions, section.conditionCombinator, ctx.values)
  ) {
    return false;
  }
  const condValues = section
    ? scopeValuesToInstance(section, ctx.values, instance, ctx.allSections)
    : ctx.values;
  if (
    (field.conditions?.length ?? 0) > 0 &&
    !evaluateConditions(field.conditions, field.conditionCombinator, condValues)
  ) {
    return false;
  }
  return true;
}

/**
 * A uniqueness collision, with the STEP the offending peer lives on.
 *
 * The step matters because peer labels repeat: RDSP has six fields called
 * "Mobile Phone" in one group, so naming the label alone produces
 * "This value is already used by 'Mobile Phone'" while the advisor is
 * standing on a field called Mobile Phone — which reads as the field
 * accusing itself and tells them nothing about where to go and fix it.
 */
export type UniquenessConflict = {
  peer: WorkflowField;
  step: WorkflowStep;
};

/**
 * Given a WorkflowField and the current advisor values map, walk every
 * peer field SHARING AT LEAST ONE of its `uniquenessGroup`s across every
 * step of the workflow, and return the first peer that already holds
 * the current field's value. Returns null when the field isn't in a
 * group, when its value is blank, or when no peer collides.
 *
 * `uniquenessGroup` is a comma-separated list ({@link uniquenessGroupsOf}), so
 * peering is set INTERSECTION, not string equality: a field in `a,b` is a peer of
 * one in `b` and of one in `a,c`, but not of one in `c`. That is what lets BMO's
 * RIF SIN rule hold the investor against both the spouse and the successor
 * annuitant while leaving spouse↔successor free to match.
 *
 * §1 Repeatable Sections: for a field placed inside a repeatable
 * section, the effective value lives at `inst:<sectionId>:0:<fieldId>`
 * (instance 0 — the callsite is FieldRenderer's on-blur which doesn't
 * carry an instance identity today). Peer walkers check EVERY instance
 * of every peer's section, so a duplicate in a repeated section blocks
 * on blur even when the peer is a sibling in the same section.
 *
 * Comparison is case-insensitive and trims whitespace — real-world
 * signatory email + phone inputs vary in casing / trailing spaces and
 * BMO treats those as identical. If we ever add a group whose values
 * are case-sensitive (e.g. serial numbers), we'll widen the API to
 * take a normalizer.
 *
 * Visibility-aware when `isFieldVisible` is supplied: hidden peers
 * don't count, so an inline on-blur error never accuses a field the
 * advisor can't see. FormShell passes its precomputed visibility
 * closure; direct callers may omit it (older paths). The peer's
 * repeatable INSTANCE is handed to that closure — a field gated from
 * inside its own repeatable section is visible per-instance, and judging
 * it against instance 0 (or the unscoped map) reads every such gate as
 * unset and silently exempts the field from uniqueness entirely.
 */
export function findUniquenessConflict(
  field: WorkflowField,
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  isFieldVisible?: (
    field: WorkflowField,
    stepId: string,
    sectionIndex: number,
    instance: number,
  ) => boolean,
  /**
   * On-blur overlay — the incoming value the advisor is committing.
   * When set, this replaces the stored own-value in the comparison
   * without the caller needing to know which qualified key the field's
   * value lives at. Peers are still read from `values` as-is.
   */
  overrideOwnValue?: FieldValue,
  /**
   * The exact value-map key of the field being checked. For a field
   * inside a repeatable section this is the CURRENT instance's key
   * (`inst:<sectionId>:<idx>:<fieldId>`), so the walker skips the right
   * instance rather than always assuming instance 0. Omit for
   * non-repeatable fields — it falls back to the bare id / instance 0.
   * Critical for correctness: without it, editing instance ≥ 1 compares
   * the field against its own stored value and always self-collides.
   */
  ownValueKey?: string,
): UniquenessConflict | null {
  const groups = uniquenessGroupsOf(field);
  if (groups.length === 0) return null;
  // Find the current field's section so we can pick the right value key.
  // Walk-and-find because passing section id through every caller would
  // ripple far; this is one blur at a time so cost is negligible.
  const ownSection = findOwningSection(field, template);
  const ownKey = ownValueKey ?? (ownSection ? valueKeyFor(ownSection, field.id) : field.id);
  const own =
    overrideOwnValue !== undefined ? normalize(overrideOwnValue) : normalize(values[ownKey]);
  if (own === null) return null;
  const ownInstance = instanceOfKey(ownKey);
  const prefillIndex = buildPrefillIndex(template, values);
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (let si = 0; si < step.sections.length; si++) {
      const section = step.sections[si];
      if (!section) continue;
      let peerHit: UniquenessConflict | null = null;
      forEachSectionField(section, values, (peerFieldId, valueKey, peerInstance) => {
        if (peerHit) return;
        const peer = findFieldById(section, peerFieldId);
        if (!peer) return;
        if (peer.id === field.id) {
          // Same authored field, different instance: still counts as a
          // peer for uniqueness (two AH instances sharing an email). Skip
          // ONLY the field's own instance — `ownKey` is the current
          // instance's key, so sibling instances still collide but the
          // field never flags against itself.
          if (section.id === ownSection?.id && valueKey === ownKey) {
            return;
          }
        }
        // Peers share at least one group — see the set-intersection note above.
        if (!uniquenessGroupsOf(peer).some((g) => groups.includes(g))) return;
        if (
          isActivePrefillCopy(
            { field, instance: ownInstance },
            { field: peer, instance: peerInstance },
            prefillIndex,
          )
        )
          return;
        // `peerInstance`, not instance 0: a peer gated from inside its own
        // repeatable section is only visible in the instance whose values
        // satisfy the gate.
        if (isFieldVisible && !isFieldVisible(peer, step.id, si, peerInstance)) return;
        const other = normalize(values[valueKey]);
        if (other === null) return;
        if (other === own) peerHit = { peer, step };
      });
      if (peerHit) return peerHit;
    }
  }
  return null;
}

/**
 * Return every uniqueness-group conflict on the workflow as pairs of
 * peer field ids. Used by FormShell's step-completion / missing-fields
 * logic to block Next when any two peers share the same value. The
 * output is a set of field ids that participate in at least one
 * collision — the caller shows an error against each. Section / field
 * visibility is respected: a hidden peer doesn't count.
 *
 * §1 Repeatable Sections: buckets receive one entry per (field,
 * instance) so two instances of the same repeatable section flagged
 * with the same email collide.
 *
 * A field carrying several groups (`"rifSin,rifSinSuccessor"`) lands in EVERY
 * one of its buckets, so it is compared against each peer set independently —
 * which is the whole point: two peers of the same field need not be peers of
 * each other.
 */
export function findAllUniquenessConflicts(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  isFieldVisible: (
    field: WorkflowField,
    stepId: string,
    sectionIndex: number,
    instance: number,
  ) => boolean,
): Set<string> {
  const byGroup = new Map<string, Array<Occurrence & { value: string }>>();
  const prefillIndex = buildPrefillIndex(template, values);
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (let si = 0; si < step.sections.length; si++) {
      const section = step.sections[si];
      if (!section) continue;
      forEachSectionField(section, values, (fieldId, valueKey, instance) => {
        const f = findFieldById(section, fieldId);
        if (!f) return;
        const groups = uniquenessGroupsOf(f);
        if (groups.length === 0) return;
        if (!isFieldVisible(f, step.id, si, instance)) return;
        const norm = normalize(values[valueKey]);
        if (norm === null) return;
        const occurrence = { field: f, instance, value: norm };
        for (const group of groups) {
          const bucket = byGroup.get(group) ?? [];
          bucket.push(occurrence);
          byGroup.set(group, bucket);
        }
      });
    }
  }
  const offenders = new Set<string>();
  for (const bucket of byGroup.values()) {
    for (let i = 0; i < bucket.length; i++) {
      for (let j = i + 1; j < bucket.length; j++) {
        const a = bucket[i]!;
        const b = bucket[j]!;
        if (a.value !== b.value || isActivePrefillCopy(a, b, prefillIndex)) continue;
        offenders.add(a.field.id);
        offenders.add(b.field.id);
      }
    }
  }
  return offenders;
}

/** One side of a uniqueness comparison: the authored field plus the repeatable-section
 *  instance the value was read from (0 for a non-repeatable section). */
type Occurrence = { field: WorkflowField; instance: number };

/** Everything the prefill-exemption test needs, built once per walk. */
type PrefillIndex = {
  sectionByFieldId: Map<string, FormSection>;
  allSections: FormSection[];
  values: Record<string, FieldValue>;
};

function buildPrefillIndex(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
): PrefillIndex {
  const allSections = allSectionsOf(template);
  const sectionByFieldId = new Map<string, FormSection>();
  for (const section of allSections) {
    for (const row of section.rows) {
      for (const col of row.columns) {
        if (col.field) sectionByFieldId.set(col.field.id, section);
      }
    }
  }
  return { sectionByFieldId, allSections, values };
}

/**
 * Two fields where one is authored as a COPY of the other (a cross-step prefill `sourceField`)
 * are never held against each other: the form itself made them equal. RDSP's "the beneficiary
 * is the account holder" flow copies the holder's email into the beneficiary's; the beneficiary
 * email can then share the holders' uniqueness group, so a DIFFERENT holder or a beneficiary who
 * is not the holder is still refused (QA BPC-013), without the copy tripping over itself.
 *
 * The exemption follows the COPY, not the wiring. A `sourceField` can be gated
 * (`conditions`) and can be narrowed to one target instance (`targetInstance`), so the link
 * existing in the template does not mean a copy is happening right now — and when no copy is
 * happening, two equal values are two people claiming the same identifier.
 *
 * RDSP's Primary Caregiver SIN is the case that forced this: it prefills from the
 * (beneficiary-is-not-the-holder) accountholder SIN, gated on "are they the primary caregiver
 * of the beneficiary?". A blanket pair exemption meant that when the advisor answered NO and
 * typed the accountholder's SIN into the caregiver by hand, nothing was copied and nothing was
 * refused. The gate is now re-evaluated here with exactly the predicate the prefill effect in
 * FormShell uses ({@link resolvePrefillSourceInstance}), so the exemption covers precisely the
 * (source instance → target instance) pair the effect would actually write.
 *
 * An UNGATED prefill still resolves to source instance 0 unconditionally, which is the
 * behaviour this replaced — the beneficiary-email copy is unaffected.
 */
export function isActivePrefillCopy(a: Occurrence, b: Occurrence, index: PrefillIndex): boolean {
  return copyIsLive(a, b, index) || copyIsLive(b, a, index);
}

/** Is `target` currently being filled from `source` — this instance of it, from that
 *  instance of the source? */
function copyIsLive(target: Occurrence, source: Occurrence, index: PrefillIndex): boolean {
  const ref = target.field.sourceField;
  if (!ref || ref.fieldId !== source.field.id) return false;
  // "Caregiver 1 only": a target instance narrows the copy to that one instance of the
  // target's repeatable section. Unset — or a non-repeatable target — means every instance.
  // Mirrors FormShell's `onlyInstance`.
  const targetSection = index.sectionByFieldId.get(target.field.id);
  if (targetSection?.repeatable) {
    const onlyInstance = ref.targetInstance ?? null;
    if (onlyInstance !== null && onlyInstance !== target.instance) return false;
  }
  const sourceInstance = resolvePrefillSourceInstance(
    ref,
    index.sectionByFieldId.get(source.field.id),
    index.values,
    index.allSections,
  );
  return sourceInstance === source.instance;
}

function findFieldById(section: FormSection, fieldId: string): WorkflowField | null {
  for (const row of section.rows) {
    for (const col of row.columns) {
      if (col.field?.id === fieldId) return col.field;
    }
  }
  return null;
}

function findOwningSection(field: WorkflowField, template: WorkflowTemplate): FormSection | null {
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections) {
      if (findFieldById(section, field.id)) return section;
    }
  }
  return null;
}

/** The repeatable-section instance a value key belongs to. A bare field id — a
 *  non-repeatable field — is instance 0. Mirrors `qualifiedKey`'s four-segment
 *  `inst:<sectionId>:<idx>:<fieldId>` shape; section and field ids are UUIDs, so a
 *  well-formed key never splits into more. */
function instanceOfKey(valueKey: string): number {
  const parts = valueKey.split(':');
  if (parts.length !== 4 || parts[0] !== 'inst') return 0;
  const idx = Number.parseInt(parts[2] ?? '', 10);
  return Number.isInteger(idx) ? idx : 0;
}

function normalize(v: FieldValue): string | null {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  if (trimmed.length === 0) return null;
  return trimmed.toLowerCase();
}
